(() => {
  'use strict';

  if (window.CottonSizePicker?.version) {
    window.CottonSizePicker.enhance(document);
    return;
  }

  const make = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const cloneThemeIcon = (name) => {
    const template = document.querySelector(`template[data-cotton-theme-icon="${name}"]`);
    return template?.content?.firstElementChild?.cloneNode(true) || null;
  };

  const formatMoney = (raw) => {
    const source = String(raw || '').trim();
    const compact = source.replace(/[^\d,.-]/g, '');
    if (!compact) return source;
    const comma = compact.lastIndexOf(',');
    const dot = compact.lastIndexOf('.');
    let normalized = compact;
    if (comma >= 0 && dot >= 0) {
      normalized = dot > comma ? compact.replace(/,/g, '') : compact.replace(/\./g, '').replace(',', '.');
    } else if (comma >= 0) {
      normalized = compact.length - comma - 1 === 2 ? compact.replace(',', '.') : compact.replace(/,/g, '');
    }
    const value = Number(normalized);
    if (!Number.isFinite(value)) return source;
    return `${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
  };

  let activeSheet = null;

  const close = (restoreFocus = true) => {
    if (!activeSheet) return;
    const { backdrop, trigger, keyHandler } = activeSheet;
    document.removeEventListener('keydown', keyHandler);
    backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('cotton-size-sheet-open');
    trigger.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => backdrop.remove(), 180);
    if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
    activeSheet = null;
  };

  const open = (select, trigger) => {
    close(false);

    const backdrop = make('div', 'cotton-suit-size-sheet');
    const panel = make('section', 'cotton-suit-size-sheet__panel');
    const panelId = `cotton-size-sheet-${Date.now()}`;
    panel.id = panelId;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', `${panelId}-title`);

    const dragHandle = make('span', 'cotton-suit-size-sheet__drag-handle');
    dragHandle.setAttribute('aria-hidden', 'true');
    const header = make('div', 'cotton-suit-size-sheet__header');
    const heading = make('div');
    const eyebrow = make('span', '', select.dataset.pickerEyebrow || 'Halı ölçüleri');
    const title = make('strong', '', select.dataset.pickerTitle || 'Ebat seçin');
    title.id = `${panelId}-title`;
    heading.append(eyebrow, title);
    const closeButton = make('button', 'cotton-suit-size-sheet__close');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Ebat seçimini kapat');
    const closeIcon = cloneThemeIcon('close');
    if (closeIcon) closeButton.append(closeIcon);
    header.append(heading, closeButton);

    const list = make('div', 'cotton-suit-size-sheet__list');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Halı ölçüleri');
    let selectedRow = null;

    [...select.options].forEach((option) => {
      const row = make('button', 'cotton-suit-size-sheet__option');
      row.type = 'button';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(option.selected));
      row.disabled = option.disabled;
      if (option.selected) {
        row.classList.add('is-selected');
        selectedRow = row;
      }

      const copy = make('span', 'cotton-suit-size-sheet__option-copy');
      copy.append(
        make('strong', '', option.dataset.optionTitle || option.textContent.trim()),
        make('span', '', option.disabled ? 'Tükendi' : formatMoney(option.dataset.price || '')),
      );
      const check = make('span', 'cotton-suit-size-sheet__check');
      check.setAttribute('aria-hidden', 'true');
      if (option.selected) {
        const successIcon = cloneThemeIcon('success');
        if (successIcon) check.append(successIcon);
      }
      row.append(copy, check);

      row.addEventListener('click', () => {
        if (option.disabled) return;
        select.value = option.value;
        const text = trigger.querySelector('[data-cotton-size-trigger-text]');
        if (text) text.textContent = option.dataset.optionTitle || option.textContent.trim();
        close(false);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      list.append(row);
    });

    panel.append(dragHandle, header, list);
    backdrop.append(panel);
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) close();
    });
    closeButton.addEventListener('click', () => close());

    let gestureStartX = 0;
    let gestureStartY = 0;
    let gestureStartedAt = 0;
    let dragDistance = 0;
    let dragging = false;
    const resetPanelPosition = () => {
      panel.style.transition = 'transform .18s ease';
      panel.style.transform = 'translateY(0)';
      backdrop.style.removeProperty('background-color');
      window.setTimeout(() => {
        if (!panel.isConnected) return;
        panel.style.removeProperty('transition');
        panel.style.removeProperty('transform');
      }, 190);
    };
    panel.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      gestureStartX = touch.clientX;
      gestureStartY = touch.clientY;
      gestureStartedAt = performance.now();
      dragDistance = 0;
      dragging = false;
    }, { passive: true });
    panel.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (list.scrollTop > 0) {
        gestureStartX = touch.clientX;
        gestureStartY = touch.clientY;
        gestureStartedAt = performance.now();
        return;
      }
      const deltaX = touch.clientX - gestureStartX;
      const deltaY = touch.clientY - gestureStartY;
      if (deltaY <= 0 || Math.abs(deltaX) > deltaY) return;
      dragging = true;
      dragDistance = Math.min(deltaY, window.innerHeight * .6);
      panel.style.transition = 'none';
      panel.style.transform = `translateY(${dragDistance}px)`;
      const shade = Math.max(.08, .46 * (1 - dragDistance / (window.innerHeight * .6)));
      backdrop.style.backgroundColor = `rgba(11, 22, 14, ${shade})`;
      if (event.cancelable) event.preventDefault();
    }, { passive: false });
    const finishDrag = () => {
      if (!dragging) return;
      const elapsed = Math.max(1, performance.now() - gestureStartedAt);
      const velocity = dragDistance / elapsed;
      dragging = false;
      if (dragDistance >= 90 || velocity >= .55) {
        panel.style.transition = 'transform .18s ease';
        panel.style.transform = 'translateY(100%)';
        close();
        return;
      }
      resetPanelPosition();
    };
    panel.addEventListener('touchend', finishDrag, { passive: true });
    panel.addEventListener('touchcancel', finishDrag, { passive: true });

    const keyHandler = (event) => {
      if (event.key === 'Escape') close();
    };
    activeSheet = { backdrop, trigger, keyHandler };
    document.addEventListener('keydown', keyHandler);
    document.documentElement.classList.add('cotton-size-sheet-open');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-controls', panelId);
    document.body.append(backdrop);
    window.requestAnimationFrame(() => {
      backdrop.classList.add('is-open');
      (selectedRow || closeButton).focus({ preventScroll: true });
      selectedRow?.scrollIntoView({ block: 'nearest' });
    });
  };

  const enhance = (root = document) => {
    root.querySelectorAll('[data-cotton-sticky-variant]:not([data-cotton-size-enhanced])').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      select.dataset.cottonSizeEnhanced = 'true';
      select.classList.add('cotton-suit-native-select');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      const trigger = make('button', 'cotton-suit-size-trigger');
      trigger.type = 'button';
      trigger.dataset.cottonSizeTrigger = 'true';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      const text = make('span', '', select.selectedOptions[0]?.dataset.optionTitle || select.selectedOptions[0]?.textContent.trim() || 'Ebat seçin');
      text.dataset.cottonSizeTriggerText = 'true';
      const chevron = make('span', 'cotton-suit-size-trigger__chevron');
      chevron.setAttribute('aria-hidden', 'true');
      const chevronIcon = cloneThemeIcon('chevron-bottom');
      if (chevronIcon) chevron.append(chevronIcon);
      trigger.append(text, chevron);
      trigger.addEventListener('click', () => open(select, trigger));
      select.insertAdjacentElement('afterend', trigger);
    });
  };

  let enhanceFrame = 0;
  const scheduleEnhance = () => {
    if (enhanceFrame) return;
    enhanceFrame = window.requestAnimationFrame(() => {
      enhanceFrame = 0;
      enhance(document);
    });
  };

  const observePickerInsertions = () => {
    if (!document.body || document.documentElement.dataset.cottonSizePickerWatch === 'true') return;
    document.documentElement.dataset.cottonSizePickerWatch = 'true';
    new MutationObserver((records) => {
      const pickerAdded = records.some((record) => [...record.addedNodes].some((node) => (
        node instanceof Element
        && (node.matches('[data-cotton-sticky-variant]') || node.querySelector('[data-cotton-sticky-variant]'))
      )));
      if (pickerAdded) scheduleEnhance();
    }).observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    enhance(document);
    observePickerInsertions();
  };

  window.CottonSizePicker = { version: '1.0.1', enhance, open, close };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
